// apps/dashboard/src/hooks/useChat.ts
import { useCallback } from 'react';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import type { ChatStreamEvent, ChatAttachment } from '@freelancer-os/shared-types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export function useChat() {
  const messages = useChatStore((s) => s.messages);
  const status = useChatStore((s) => s.status);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const artifacts = useChatStore((s) => s.artifacts);
  const error = useChatStore((s) => s.error);

  const sendMessage = useCallback(
    async (message: string, attachments?: ChatAttachment[]) => {
      // Use getState() to avoid stale closures — standard Zustand pattern for async callbacks
      const store = useChatStore.getState();
      store.addUserMessage(message);
      store.setIsStreaming(true);
      store.setError(null);

      const assistantId = store.startAssistantMessage();
      const token = useAuthStore.getState().accessToken;

      try {
        const response = await fetch(`${API_BASE}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message,
            threadId: useChatStore.getState().threadId,
            ...(attachments && attachments.length > 0 ? { attachments } : {}),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();

            if (data === '[DONE]') {
              store.finalizeAssistantMessage(assistantId);
              store.setStatus(null);
              store.setIsStreaming(false);
              return;
            }

            try {
              const event: ChatStreamEvent = JSON.parse(data);

              // Re-get state for each event to avoid stale closure
              const s = useChatStore.getState();
              switch (event.type) {
                case 'thread':
                  s.setThreadId(event.threadId);
                  break;
                case 'status':
                  s.setStatus(event.message);
                  break;
                case 'token':
                  s.setStatus(null); // Hide status when tokens arrive
                  s.appendToken(assistantId, event.content);
                  break;
                case 'artifact':
                  s.appendArtifact(event.data);
                  break;
                case 'error':
                  s.setError(event.message);
                  break;
              }
            } catch {
              // Skip unparseable lines
            }
          }
        }

        // If stream ended without [DONE]
        store.finalizeAssistantMessage(assistantId);
        store.setIsStreaming(false);
      } catch (err) {
        const s = useChatStore.getState();
        s.setError(err instanceof Error ? err.message : 'Connection failed');
        s.finalizeAssistantMessage(assistantId);
        s.setIsStreaming(false);
      }
    },
    [], // No deps needed — we use getState() inside
  );

  return { messages, status, isStreaming, artifacts, error, sendMessage };
}
